import { userRoles } from "@/lib/common";

export const HotjarEvent = {
  USER_LOGGED_IN: "user_logged_in",
  USER_LOGGED_OUT: "user_logged_out",
  COMPANY_SELECTED: "company_selected",
  HOME_VIEWED: "home_viewed",
  HOME_ACCOUNTS_TAB_VIEWED: "home_accounts_tab_viewed",
  HOME_ANALYTICS_TAB_VIEWED: "home_analytics_tab_viewed",
  DASHBOARD_VIEWED: "dashboard_viewed",
  INSIGHT_HUB_VIEWED: "insight_hub_viewed",
  CUSTOM_DASHBOARD_VIEWED: "custom_dashboard_viewed",
  PORTFOLIO_VIEWED: "portfolio_viewed",
  MESSAGES_VIEWED: "messages_viewed",
  RECOVERY_VIEWED: "recovery_viewed",
  COACH_VIEWED: "coach_viewed",
  GAME_VIEWED: "game_viewed",
  SETTINGS_VIEWED: "settings_viewed",
  SETTINGS_ACCOUNT_VIEWED: "settings_account_viewed",
  SETTINGS_MANAGE_USERS_VIEWED: "settings_manage_users_viewed",
  SETTINGS_BILLING_VIEWED: "settings_billing_viewed",
  SETTINGS_ESTIMATE_AVERAGES_VIEWED: "settings_estimate_averages_viewed",
  SETTINGS_RECOVERY_STEPS_VIEWED: "settings_recovery_steps_viewed",
  SETTINGS_RR_SETTINGS_VIEWED: "settings_rr_settings_viewed",
  SETTINGS_NOTIFICATIONS_VIEWED: "settings_notifications_viewed",
  SETTINGS_CONFIGURATION_VIEWED: "settings_configuration_viewed",
  SETTINGS_MISC_VIEWED: "settings_misc_viewed",
  WEEKLY_REPORT_GENERATED: "weekly_report_generated",
  WEEKLY_REPORT_DOWNLOADED: "weekly_report_downloaded",
  CHATBOT_OPENED: "chatbot_opened",
} as const;

export type HotjarEventName = (typeof HotjarEvent)[keyof typeof HotjarEvent];

/** Settings sidebar tab id → Hotjar event */
export const SETTINGS_TAB_EVENTS: Record<string, HotjarEventName> = {
  account: HotjarEvent.SETTINGS_ACCOUNT_VIEWED,
  "manage-users": HotjarEvent.SETTINGS_MANAGE_USERS_VIEWED,
  billing: HotjarEvent.SETTINGS_BILLING_VIEWED,
  "historical-averages": HotjarEvent.SETTINGS_ESTIMATE_AVERAGES_VIEWED,
  workflow: HotjarEvent.SETTINGS_RECOVERY_STEPS_VIEWED,
  "rr-settings": HotjarEvent.SETTINGS_RR_SETTINGS_VIEWED,
  notifications: HotjarEvent.SETTINGS_NOTIFICATIONS_VIEWED,
  configuration: HotjarEvent.SETTINGS_CONFIGURATION_VIEWED,
  misc: HotjarEvent.SETTINGS_MISC_VIEWED,
};

/** Map authenticated app paths to a page-view event (null = no page event). */
export function getPageViewEventFromPathname(pathname: string | null): HotjarEventName | null {
  if (!pathname) return null;
  if (pathname === "/home" || pathname.startsWith("/home/")) {
    return HotjarEvent.HOME_VIEWED;
  }

  // /{companyId}/{section}/...
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 2) return null;
  const section = parts[1];

  switch (section) {
    case "dashboard":
    case "dashboard2":
      return HotjarEvent.DASHBOARD_VIEWED;
    case "insight":
      return HotjarEvent.INSIGHT_HUB_VIEWED;
    case "custom-dashboard":
      return HotjarEvent.CUSTOM_DASHBOARD_VIEWED;
    case "source":
      return HotjarEvent.PORTFOLIO_VIEWED;
    case "chat":
      return HotjarEvent.MESSAGES_VIEWED;
    case "recovery":
      return HotjarEvent.RECOVERY_VIEWED;
    case "coach":
      return HotjarEvent.COACH_VIEWED;
    case "game":
      return HotjarEvent.GAME_VIEWED;
    case "settings":
      return HotjarEvent.SETTINGS_VIEWED;
    default:
      return null;
  }
}

export type HotjarRoleLabel = "Super Admin" | "Client Admin" | "CSR";

export type HotjarIdentifyAttributes = {
  role: HotjarRoleLabel | string;
  userName?: string;
  email?: string;
  companyId?: string;
  companyName?: string;
};

/** Contentsquare Tag ID — same value used by ContentsquareInit. */
const TAG_ID = process.env.NEXT_PUBLIC_CONTENTSQUARE_ID || "2162f9e9f61ff";
/** Set NEXT_PUBLIC_HOTJAR_DEBUG=true to allow tracking on local `npm run dev`. */
const DEBUG_ENABLED = process.env.NEXT_PUBLIC_HOTJAR_DEBUG === "true";
const DEDUPE_STORAGE_PREFIX = "hj_event:";

/** Custom-var indexes (1–20) for Contentsquare identify attributes. */
const CUSTOM_VAR = {
  ROLE: 1,
  USER_NAME: 2,
  EMAIL: 3,
  COMPANY_ID: 4,
  COMPANY_NAME: 5,
} as const;

let isInitialized = false;
let isIdentified = false;
let lastCompanySelectedId: string | null = null;
const firedEvents = new Set<string>();

function isEnabled(): boolean {
  if (!TAG_ID) return false;
  return process.env.NODE_ENV === "production" || DEBUG_ENABLED;
}

function ensureUxa(): void {
  if (typeof window === "undefined") return;
  window._uxa = window._uxa || [];
}

function pushUxa(...args: unknown[]): void {
  ensureUxa();
  window._uxa!.push(args);
}

function getDedupeKey(eventName: HotjarEventName, dedupeKey?: string): string {
  return dedupeKey ? `${eventName}:${dedupeKey}` : eventName;
}

function hasFired(key: string): boolean {
  if (firedEvents.has(key)) return true;
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(`${DEDUPE_STORAGE_PREFIX}${key}`) === "1";
  } catch {
    return false;
  }
}

function markFired(key: string): void {
  firedEvents.add(key);
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(`${DEDUPE_STORAGE_PREFIX}${key}`, "1");
  } catch {
    // sessionStorage may be unavailable; in-memory set still prevents remount dupes
  }
}

function clearDedupeState(): void {
  firedEvents.clear();
  lastCompanySelectedId = null;
  if (typeof window === "undefined") return;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(DEDUPE_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

export function toHotjarRole(role: string): HotjarRoleLabel | string {
  switch (role) {
    case userRoles.SUPER_ADMIN:
      return "Super Admin";
    case userRoles.CLIENT_ADMIN:
      return "Client Admin";
    case userRoles.CSR_USER:
    case userRoles.SUPER_CSR_ADMIN:
      return "CSR";
    default:
      return role;
  }
}

/**
 * Ready when Contentsquare Tag ID is configured.
 * Script injection is handled by ContentsquareInit — do not load Hotjar CDN.
 */
function init(): boolean {
  if (typeof window === "undefined") return false;
  if (!isEnabled()) return false;
  if (isInitialized) return true;

  ensureUxa();
  isInitialized = true;
  return true;
}

function setVisitCustomVar(index: number, name: string, value: string): void {
  // Scope 2 = visit — persists for the Contentsquare session
  pushUxa("setCustomVariable", index, name, value, 2);
}

function identifyUser(userId: string, attributes: HotjarIdentifyAttributes): void {
  if (!userId) return;
  if (!init()) return;

  // Session Replay filter key (works with standard Contentsquare tag)
  pushUxa("trackPageEvent", `@user-identifier@${userId}`);
  // Product Analytics identity (queued; no-op if PA/Heap is not enabled)
  pushUxa("identify", userId);
  pushUxa("addUserProperties", {
    role: attributes.role,
    ...(attributes.userName ? { userName: attributes.userName } : {}),
    ...(attributes.email ? { email: attributes.email } : {}),
    ...(attributes.companyId ? { companyId: attributes.companyId } : {}),
    ...(attributes.companyName ? { companyName: attributes.companyName } : {}),
  });

  setVisitCustomVar(CUSTOM_VAR.ROLE, "role", String(attributes.role));
  if (attributes.userName) {
    setVisitCustomVar(CUSTOM_VAR.USER_NAME, "userName", attributes.userName);
  }
  if (attributes.email) {
    setVisitCustomVar(CUSTOM_VAR.EMAIL, "email", attributes.email);
  }
  if (attributes.companyId) {
    setVisitCustomVar(CUSTOM_VAR.COMPANY_ID, "companyId", attributes.companyId);
  }
  if (attributes.companyName) {
    setVisitCustomVar(CUSTOM_VAR.COMPANY_NAME, "companyName", attributes.companyName);
  }

  isIdentified = true;
}

function setCompanyContext(companyId: string, companyName: string): void {
  if (!init()) return;
  setVisitCustomVar(CUSTOM_VAR.COMPANY_ID, "companyId", companyId);
  setVisitCustomVar(CUSTOM_VAR.COMPANY_NAME, "companyName", companyName);
}

function trackPageEvent(name: HotjarEventName): void {
  pushUxa("trackPageEvent", name);
}

function event(name: HotjarEventName, options?: { dedupeKey?: string }): void {
  if (!init()) return;

  if (name === HotjarEvent.COMPANY_SELECTED) {
    const companyId = options?.dedupeKey;
    if (!companyId || companyId === lastCompanySelectedId) return;
    lastCompanySelectedId = companyId;
    trackPageEvent(name);
    return;
  }

  // When a dedupeKey is provided, fire at most once per key per tab session.
  // Without a key (e.g. chatbot_opened), fire every call — callers guard transitions.
  if (options?.dedupeKey) {
    const key = getDedupeKey(name, options.dedupeKey);
    if (hasFired(key)) return;
    trackPageEvent(name);
    markFired(key);
    return;
  }

  trackPageEvent(name);
}

function reset(): void {
  isIdentified = false;
  clearDedupeState();

  if (typeof window === "undefined") return;
  if (!isInitialized) return;

  // Clear identity for the current Contentsquare session before navigating to login.
  pushUxa("resetIdentity");
}

/** Fire when a user opens a saved weekly report in the client UI. */
export function trackWeeklyReportGenerated(reportId: string): void {
  if (!reportId) return;
  event(HotjarEvent.WEEKLY_REPORT_GENERATED, { dedupeKey: reportId });
}

/** Fire after a successful weekly-report PDF download. */
export function trackWeeklyReportDownloaded(reportId: string): void {
  if (!reportId) return;
  event(HotjarEvent.WEEKLY_REPORT_DOWNLOADED, { dedupeKey: reportId });
}

/** Fire a settings-tab view once per company + tab per session. */
export function trackSettingsTabViewed(companyId: string, tabId: string): void {
  if (!companyId || !tabId) return;
  const tabEvent = SETTINGS_TAB_EVENTS[tabId];
  if (!tabEvent) return;
  event(tabEvent, { dedupeKey: `${companyId}:${tabId}` });
}

export const hotjar = {
  init,
  identifyUser,
  setCompanyContext,
  event,
  reset,
  trackWeeklyReportGenerated,
  trackWeeklyReportDownloaded,
  trackSettingsTabViewed,
  isReady: () => isInitialized,
  isUserIdentified: () => isIdentified,
};

export default hotjar;
